/***
 * Copyright (c) Myia 2023-2025 - All Rights Reserved
 */
/* eslint-disable max-len */

import { Editor } from '@monaco-editor/react';
import { CaretSortIcon } from '@radix-ui/react-icons';
import { Code2, CurlyBraces, DramaIcon, PersonStanding, Play, Settings2, Sparkles } from 'lucide-react';
import React, { cloneElement, Dispatch, FC, PropsWithChildren, ReactElement, useEffect, useState } from 'react';

import { CollapsibleSection } from './CollapsibleSection';
import { getStepParams, isStepExecutable, ParamsMap } from './LiveAgentSessionUtils';
import { actionProps, Step } from './LiveAgentTypes';
import { VariablesPopover } from './VariablesPopover';

import { TargetingStrategy } from '@/commons/data';
import { Button } from '@/components/atoms/Button';
import { Button as Clickable } from '@/components/ui/button';
import { Command, CommandGroup, CommandItem, CommandList } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

export const AtomicActionForm: FC<
    PropsWithChildren<{
        step: Step;
        executeStep: (step: Step, index: number, parentIndex?: number) => void;
        index: number;
        parentIndex?: number;
        isDisabled: boolean;
        dispatch: Dispatch<any>;
        setQueue?: (index: number, parentIndex?: number) => void;
        resetQueue?: () => void;
        highlightSelectors?: (
            selectors: { [key: string]: { selector: string; unique: boolean } } | null,
            force?: boolean,
        ) => void;
        variables: { [key: string]: string };
        editVariables?: (action: 'add' | 'remove', payload: { [key: string]: string }) => void;
        isFullScreen?: boolean;
        isLayerOpen?: boolean;
        closeLayer?: () => void;
    }>
> = ({
    step,
    executeStep,
    index,
    parentIndex,
    isDisabled,
    dispatch,
    setQueue,
    resetQueue,
    children,
    highlightSelectors,
    variables,
    editVariables,
    isFullScreen,
    isLayerOpen,
    closeLayer = () => {},
}) => {
    const [isRunButtonHovered, setIsRunButtonHovered] = useState(false);
    const [isShiftKeyHeld, setIsShiftKeyHeld] = useState(false);

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Shift') setIsShiftKeyHeld(true);
        };
        const handleKeyUp = (e: KeyboardEvent) => {
            if (e.key === 'Shift') setIsShiftKeyHeld(false);
        };

        window.addEventListener('keydown', handleKeyDown);
        window.addEventListener('keyup', handleKeyUp);

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
            window.removeEventListener('keyup', handleKeyUp);
        };
    }, []);

    useEffect(() => {
        if (isRunButtonHovered) {
            setQueue?.(index, parentIndex);
        }
    }, [isRunButtonHovered, isShiftKeyHeld]);

    const stepType = step.action ?? step.kind;
    const { paramsMap } = actionProps[stepType as keyof typeof actionProps];

    const stepParams = getStepParams(step, paramsMap as ParamsMap);

    const [target, setTarget] = useState<string | number>(stepParams.target ?? '');
    const [value, setValue] = useState<string>(stepParams.value ?? '');
    const [intent, setIntent] = useState<string>(step.intent ?? '');
    const [address, setAddress] = useState<string>(step.address ?? '');
    const [typingMode, setTypingMode] = useState<'fill' | 'pressSequentially' | string>(
        stepParams.typingMode ?? 'fill',
    );
    const [operator, setOperator] = useState<
        'equals' | 'notEquals' | 'contains' | 'notContains' | 'matches' | 'notMatches' | string
    >(stepParams.operator ?? '');
    const [assetType, setAssetType] = useState<'microphone' | 'camera' | 'fileUpload' | string>(
        stepParams.assetType ?? '',
    );
    const [assetUrl, setAssetUrl] = useState<string>(stepParams.assetUrl ?? '');

    const [targetingStrategy, setTargetingStrategy] = useState<
        'ai' | 'css' | 'xpath' | 'datatestid' | 'arialabel' | 'playwright'
    >(step.targetingStrategy ?? 'ai');

    const params = {
        target,
        value,
        intent,
        operator,
        typingMode,
        address,
    };

    useEffect(() => {
        const data: {
            [key: string]: string | number;
        } = {};
        for (const key of Object.keys(paramsMap) as Array<keyof typeof paramsMap>) {
            data[paramsMap[key]] = params[key];
        }

        dispatch({
            type: 'update',
            payload: {
                id: step.id,
                data,
            },
        });
    }, [target, value, intent, operator, typingMode, address]);

    useEffect(() => {
        if (assetUrl && assetType) {
            dispatch({
                type: 'update',
                payload: {
                    id: step.id,
                    data: { mediaAssets: { [assetType]: { url: assetUrl } } },
                },
            });
        } else {
            dispatch({
                type: 'update',
                payload: {
                    id: step.id,
                    data: { mediaAssets: null },
                },
            });
        }
    }, [assetUrl, assetType]);

    useEffect(() => {
        dispatch({
            type: 'update',
            payload: {
                id: step.id,
                data: { targetingStrategy },
            },
        });
    }, [targetingStrategy]);

    useEffect(() => {
        if (step.kind !== 'aiAtomic') {
            return;
        }
        if (targetingStrategy && targetingStrategy !== 'ai' && target) {
            highlightSelectors?.(getHighlightableSelectorsForTargetingStrategy(targetingStrategy, `${target}`), true);
        } else {
            highlightSelectors?.(null, true);
        }
    }, [targetingStrategy, target]);

    useEffect(() => {
        if (step.kind === 'emailSignup') {
            if (intent !== 'confirmationCode-save') {
                setValue('');
            }
        }
    }, [intent]);

    const inputProps = {
        className:
            'rounded-md border border-slate-300 bg-white placeholder:text-slate-400 p-1 flex-1 min-w-0 w-full h-10',
    };

    const handleSubmit = () => {
        resetQueue?.();
        executeStep(step, index, parentIndex);
    };

    useEffect(() => {
        if (step.pwCode) {
            setTarget(step.pwCode);
        }
    }, [step.pwCode]);

    const getButtonTitle = () => {
        if (isDisabled) {
            return 'The step is disabled';
        }
        if (!isStepExecutable(step)) {
            return 'The step is not executable';
        }
        return undefined;
    };

    const runButton = (
        <Button
            type="submit"
            disabled={isDisabled || !isStepExecutable(step)}
            title={getButtonTitle()}
            onMouseEnter={() => setIsRunButtonHovered(true)}
            onMouseLeave={() => {
                setIsRunButtonHovered(false);
                resetQueue?.();
            }}
            className="mt-1 ms-auto block"
            color="white"
            size="small"
        >
            <span className="flex items-center gap-x-1">
                <Play className="text-purple-400" size="16px" />
                Run
            </span>
        </Button>
    );

    const fileUploadForm = (
        <CollapsibleSection
            title={
                <span className="flex items-center gap-x-2">
                    <Settings2 size={16} />
                    Options
                </span>
            }
            containerClassName="pt-0 pb-0"
            headerClassName="h-[40px]"
            theme="small"
        >
            <div className="py-1">
                <div className="flex items-center gap-x-2">
                    <select
                        className="rounded-md border border-slate-300 bg-white placeholder:text-slate-400 p-1 self-stretch"
                        value={assetType}
                        onChange={(e) => setAssetType(e.target.value as 'microphone' | 'camera' | 'fileUpload')}
                    >
                        <option value="">Select an asset type</option>
                        <option value="fileUpload">File upload</option>
                        <option value="microphone">Microphone</option>
                        <option value="camera">Camera</option>
                    </select>
                    <input
                        type="text"
                        {...inputProps}
                        value={assetUrl}
                        placeholder="URL of the file you wish to upload"
                        onChange={(e) => setAssetUrl(e.target.value)}
                    />
                </div>
            </div>
        </CollapsibleSection>
    );

    let content;

    const targetingSystem = (
        <TargetingStrategySwitcher
            value={targetingStrategy}
            handleSelect={(val) => setTargetingStrategy(val as typeof targetingStrategy)}
        />
    );

    switch (stepType) {
        case 'pressKey':
            content = (
                <div className="space-y-1">
                    <div className="flex items-center gap-x-1">
                        <label id={`action-target-label-${index}`} aria-label={`The key to press`}>
                            Key:{' '}
                        </label>
                        <input
                            type="text"
                            {...inputProps}
                            value={value}
                            autoFocus
                            aria-labelledby={`action-target-value-${index}`}
                            placeholder="Example: Escape"
                            onChange={(e) => setValue(e.target.value)}
                        />
                    </div>
                </div>
            );
            break;
        case 'leftClick':
        case 'hover':
        case 'pressEnter':
            content = (
                <div className="space-y-1">
                    <div className="flex items-center gap-x-1">
                        <label
                            className="w-[40px] text-right"
                            id={`action-target-label-${index}`}
                            aria-label={`The target element to ${stepType} on`}
                        >
                            On:{' '}
                        </label>
                        <CustomActionInput
                            input={
                                <input
                                    type="text"
                                    {...inputProps}
                                    value={target}
                                    autoFocus
                                    aria-labelledby={`action-target-label-${index}`}
                                    placeholder="Example: The first item in the top navigation"
                                    onChange={(e) => setTarget(e.target.value)}
                                />
                            }
                            variables={variables}
                            editVariables={editVariables}
                            targetingSystem={targetingSystem}
                            targetingStrategy={targetingStrategy}
                        />
                    </div>
                    {targetingStrategy !== 'ai' && (
                        <div className="flex items-center gap-x-2">
                            Description:{' '}
                            <CustomActionInput
                                input={
                                    <input
                                        type="text"
                                        {...inputProps}
                                        value={intent}
                                        autoFocus
                                        placeholder="(optional) Describe the target element. Helps the agent maintain tests."
                                        onChange={(e) => setIntent(e.target.value)}
                                    />
                                }
                                variables={variables}
                                editVariables={editVariables}
                            />
                        </div>
                    )}
                </div>
            );
            break;
        case 'typeText':
            content = (
                <div className="space-y-1">
                    <div className="flex items-center gap-1">
                        <label
                            className="w-[40px] text-right"
                            id="type-action-target-label"
                            aria-label="The target element to type on"
                        >
                            On:
                        </label>
                        <CustomActionInput
                            input={
                                <input
                                    type="text"
                                    {...inputProps}
                                    value={target}
                                    autoFocus
                                    aria-labelledby="type-action-target-label"
                                    placeholder="Example: The search bar in the header"
                                    onChange={(e) => setTarget(e.target.value)}
                                />
                            }
                            variables={variables}
                            editVariables={editVariables}
                            targetingSystem={targetingSystem}
                            targetingStrategy={targetingStrategy}
                        />
                    </div>
                    {targetingStrategy !== 'ai' && (
                        <div className="flex items-center gap-x-1">
                            <label
                                className="w-[80px] text-right"
                                id="type-action-target-description"
                                aria-label="A user-friendly description of the target element to help the agent maintain tests"
                            >
                                Description:{' '}
                            </label>
                            <CustomActionInput
                                input={
                                    <input
                                        type="text"
                                        {...inputProps}
                                        value={intent}
                                        placeholder="(optional) Describe the target element. Helps the agent maintain tests."
                                        onChange={(e) => setIntent(e.target.value)}
                                    />
                                }
                                variables={variables}
                                editVariables={editVariables}
                            />
                        </div>
                    )}
                    <div className="flex items-center gap-1">
                        <label
                            className="w-[40px] text-right"
                            id="type-action-target-value"
                            aria-label="The value to type on the target element"
                        >
                            Type:
                        </label>
                        <CustomActionInput
                            input={
                                <input
                                    type="text"
                                    {...inputProps}
                                    value={value}
                                    aria-labelledby="type-action-target-value"
                                    autoFocus
                                    onChange={(e) => setValue(e.target.value)}
                                    placeholder="The text you wish to type"
                                />
                            }
                            variables={variables}
                            editVariables={editVariables}
                        />
                    </div>
                    <div className="flex items-center gap-1">
                        <label className="w-[40px] text-right" id="type-action-mode-label" aria-label="The typing mode">
                            Mode:
                        </label>
                        <select
                            className="rounded-md border border-slate-300 bg-slate-50 placeholder:text-slate-400 p-1 self-stretch w-full flex-1"
                            data-testid="typing-mode"
                            onChange={(e) => setTypingMode(e.target.value)}
                            value={typingMode ?? 'fill'}
                        >
                            <option value="fill">Fill</option>
                            <option value="pressSequentially">Press Sequentially</option>
                        </select>
                    </div>
                </div>
            );
            break;
        case 'selectOption':
            content = (
                <div className="space-y-1">
                    <div className="flex items-center gap-1">
                        <label
                            className="w-[40px] text-right"
                            id={`select-option-target-label-${index}`}
                            aria-label="The target element to click on to open the dropdown"
                        >
                            In:{' '}
                        </label>
                        <CustomActionInput
                            input={
                                <input
                                    type="text"
                                    aria-describedby={`select-option-target-label-${index}`}
                                    {...inputProps}
                                    value={target}
                                    onChange={(e) => setTarget(e.target.value)}
                                    placeholder='Example: The "Select a country" dropdown'
                                />
                            }
                            variables={variables}
                            editVariables={editVariables}
                            targetingSystem={targetingSystem}
                            targetingStrategy={targetingStrategy}
                        />
                    </div>
                    {targetingStrategy !== 'ai' && (
                        <div className="flex items-center gap-x-1">
                            <label
                                className="w-[40px] text-right"
                                id={`select-option-target-description-${index}`}
                                aria-label="A user friendly descriptio of t target element to click on to open the dropdown, to help the agent maintain tests"
                            >
                                Description:{' '}
                            </label>

                            <CustomActionInput
                                input={
                                    <input
                                        type="text"
                                        aria-describedby={`select-option-target-description-${index}`}
                                        {...inputProps}
                                        value={intent}
                                        autoFocus
                                        placeholder="(optional) Describe the target element. Helps the agent maintain tests."
                                        onChange={(e) => setIntent(e.target.value)}
                                    />
                                }
                                variables={variables}
                                editVariables={editVariables}
                            />
                        </div>
                    )}
                    <div className="flex items-center gap-1">
                        <label
                            className="w-[40px] text-right"
                            id={`select-option-value-label-${index}`}
                            aria-label="The dropdown option to select"
                        >
                            Select
                        </label>
                        <CustomActionInput
                            input={
                                <input
                                    type="text"
                                    aria-describedby={`select-option-value-label-${index}`}
                                    {...inputProps}
                                    value={value}
                                    autoFocus
                                    onChange={(e) => setValue(e.target.value)}
                                    placeholder="The option to select"
                                />
                            }
                            variables={variables}
                            editVariables={editVariables}
                        />
                    </div>
                </div>
            );
            break;
        case 'gotoUrl':
            content = (
                <div className="flex items-center gap-x-2">
                    <CustomActionInput
                        input={
                            <input
                                type="text"
                                {...inputProps}
                                placeholder="Move page to this URL"
                                value={target}
                                autoFocus
                                onChange={(e) => setTarget(e.target.value)}
                            />
                        }
                        variables={variables}
                        editVariables={editVariables}
                    />
                </div>
            );
            break;
        case 'emailSignup':
            content = (
                <div className="space-y-1">
                    <label>Signup method: </label>
                    <select
                        className="rounded-md border border-slate-300 bg-slate-50 placeholder:text-slate-400 p-1 self-stretch w-full"
                        value={intent}
                        onChange={(e) => {
                            const signupMethodValue = e.target.value;
                            if (
                                signupMethodValue === 'confirmationCode-save' ||
                                signupMethodValue === 'confirmationCode-type'
                            ) {
                                setTarget('confirmationCode');
                            } else {
                                setTarget(signupMethodValue);
                            }
                            setIntent(signupMethodValue);
                        }}
                    >
                        <option value="">Choose a signup method</option>
                        <option value="confirmationCode-type">
                            Confirmation code - get code from email and submit
                        </option>
                        <option value="confirmationCode-save">
                            Confirmation code - get code from email and save in variable
                        </option>
                        <option value="confirmationLink">Confirmation link</option>
                    </select>
                    {intent === 'confirmationCode-save' && (
                        <div className="flex items-center gap-x-2">
                            Store code in:{' '}
                            <input
                                type="text"
                                {...inputProps}
                                autoFocus
                                value={value}
                                placeholder="The variable to store the code in"
                                onChange={(e) => setValue(e.target.value)}
                            />
                        </div>
                    )}
                    <div className="flex items-center gap-x-2">
                        <label>(optional) Email address: </label>
                        <CustomActionInput
                            input={
                                <input
                                    type="text"
                                    {...inputProps}
                                    value={address}
                                    autoFocus
                                    placeholder="Email address"
                                    onChange={(e) => setAddress(e.target.value)}
                                />
                            }
                            variables={variables}
                            editVariables={editVariables}
                        />
                    </div>
                </div>
            );
            break;
        case 'emailLogin':
            content = (
                <div className="flex items-center gap-x-2">
                    <CustomActionInput
                        input={
                            <input
                                type="text"
                                {...inputProps}
                                value={target}
                                autoFocus
                                placeholder="Email address"
                                onChange={(e) => setTarget(e.target.value)}
                            />
                        }
                        variables={variables}
                        editVariables={editVariables}
                    />
                </div>
            );
            break;
        case 'readSMS':
            content = (
                <div className="space-y-1">
                    <div className="flex items-center gap-x-2">
                        <label
                            className="w-[80px] text-right"
                            id="type-action-target-label"
                            aria-label="The target element to type on"
                        >
                            Number:
                        </label>
                        <CustomActionInput
                            input={
                                <input
                                    type="text"
                                    {...inputProps}
                                    value={target}
                                    autoFocus
                                    placeholder="The phone number to read from. Ask us to get one."
                                    onChange={(e) => setTarget(e.target.value)}
                                />
                            }
                            variables={variables}
                            editVariables={editVariables}
                        />
                    </div>
                    <div className="flex items-center gap-x-2">
                        <label
                            className="w-[80px] text-right"
                            id="type-action-target-label"
                            aria-label="The target element to type on"
                        >
                            Variable:
                        </label>
                        <CustomActionInput
                            input={
                                <input
                                    type="text"
                                    {...inputProps}
                                    value={value}
                                    autoFocus
                                    placeholder="The name of the variable in which to store the SMS content."
                                    onChange={(e) => setValue(e.target.value)}
                                />
                            }
                            variables={variables}
                            editVariables={editVariables}
                        />
                    </div>
                </div>
            );
            break;
        case 'simpleAssertion':
            content = (
                <div className="space-y-1">
                    <select
                        className="rounded-md border border-slate-300 bg-slate-50 placeholder:text-slate-400 p-1 self-stretch w-full flex-1"
                        onChange={(e) => setTarget(e.target.value)}
                        value={target}
                    >
                        <option value="">Choose a target</option>
                        <option value="url">URL</option>
                        <option value="title">Page Title</option>
                        <option value="pageText">Page Text</option>
                    </select>
                    <select
                        className="rounded-md border border-slate-300 bg-slate-50 placeholder:text-slate-400 p-1 self-stretch w-full flex-1"
                        onChange={(e) => setOperator(e.target.value as typeof operator)}
                        value={operator}
                    >
                        <option value="">Choose an operator</option>
                        <option value="equals">equals</option>
                        <option value="notEquals">does not equal</option>
                        <option value="contains">contains</option>
                        <option value="notContains">does not contain</option>
                        <option value="matches">matches</option>
                        <option value="notMatches">does not match</option>
                    </select>
                    <CustomActionInput
                        input={
                            <input
                                type="text"
                                {...inputProps}
                                autoFocus
                                value={value}
                                placeholder="The text you wish to find"
                                onChange={(e) => setValue(e.target.value)}
                            />
                        }
                        variables={variables}
                        editVariables={editVariables}
                    />
                </div>
            );
            break;
        case 'aiAssertion':
            content = (
                <div className="flex items-center gap-x-2">
                    <CustomActionInput
                        input={
                            <textarea
                                {...inputProps}
                                value={target}
                                autoFocus
                                placeholder="What do you want to assert?"
                                onChange={(e) => setTarget(e.target.value)}
                                style={{
                                    minHeight: '36px',
                                }}
                            />
                        }
                        variables={variables}
                        editVariables={editVariables}
                    />
                </div>
            );
            break;
        case 'execPlaywright':
            content = (
                <div className="flex items-center gap-x-2" style={{ clipPath: 'inset(0)' }}>
                    {isFullScreen ? (
                        <Editor
                            onChange={(val) => setTarget(`${val}`)}
                            defaultValue={target ? `${target}` : '// type some Playwright code here'}
                            width="100%"
                            height={'calc(100vh - 200px)'}
                            theme="vs-dark"
                            language="javascript"
                        />
                    ) : (
                        !isLayerOpen && (
                            <Editor
                                onChange={(val) => setTarget(`${val}`)}
                                defaultValue={target ? `${target}` : '// type some Playwright code here'}
                                width="100%"
                                height="200px"
                                theme="vs-dark"
                                language="javascript"
                                onMount={(editor) => {
                                    editor.getDomNode()?.scrollIntoView();
                                }}
                            />
                        )
                    )}
                </div>
            );
            break;
        case 'wait':
            content = (
                <div className="flex items-center gap-x-2">
                    <input
                        type="number"
                        {...inputProps}
                        value={Math.max(parseInt(target.toString(), 10) / 1000, 0).toString()}
                        min={0}
                        autoFocus
                        placeholder="Duration in seconds"
                        onChange={(e) => setTarget(Number(e.target.value) * 1000)}
                    />
                    seconds
                </div>
            );
            break;
        case 'aiExtractInfo':
            content = (
                <div className="space-y-1">
                    <div className="flex items-center gap-1">
                        <label
                            className="w-[40px] text-right"
                            id="extract-action-description-label"
                            aria-label="The description of the information you want to extract"
                        >
                            Store:
                        </label>
                        <input
                            type="text"
                            {...inputProps}
                            value={target}
                            autoFocus
                            aria-labelledby="extract-action-description-label"
                            placeholder="Example: The number of items in the cart"
                            onChange={(e) => setTarget(e.target.value)}
                        />
                    </div>
                    <div className="flex items-center gap-x-1">
                        <label
                            className="w-[40px] text-right"
                            id="extract-action-variable-label"
                            aria-label="The variable to store the extracted information"
                        >
                            in:{' '}
                        </label>
                        <input
                            type="text"
                            aria-labelledby='extract-action-variable-label"'
                            {...inputProps}
                            value={value}
                            placeholder="The variable to store the extracted information in"
                            onChange={(e) => setValue(e.target.value)}
                        />
                    </div>
                </div>
            );
            break;
        case 'extractSelector':
            content = (
                <div className="space-y-1">
                    <div className="flex items-center gap-1">
                        <label
                            className="w-[40px] text-right"
                            id="extract-selector-description-label"
                            aria-label="The description of the element whose selector you want to extract"
                        >
                            Store:
                        </label>
                        <input
                            type="text"
                            {...inputProps}
                            value={target}
                            autoFocus
                            aria-labelledby="extract-selector-description-label"
                            placeholder="Example: The search input in the header"
                            onChange={(e) => setTarget(e.target.value)}
                        />
                    </div>
                    <div className="flex items-center gap-x-1">
                        <label
                            className="w-[40px] text-right"
                            id="extract-selector-variable-label"
                            aria-label="The variable to store the extracted selector"
                        >
                            in:{' '}
                        </label>
                        <input
                            type="text"
                            aria-labelledby="extract-selector-variable-label"
                            {...inputProps}
                            value={value}
                            placeholder="The variable to store the extracted selector in"
                            onChange={(e) => setValue(e.target.value)}
                        />
                    </div>
                </div>
            );
            break;
        default:
            content = 'Unknown';
            break;
    }

    return (
        <form
            onSubmit={(e) => {
                e.preventDefault();
                // if a layer is open, close it
                closeLayer && closeLayer();
                handleSubmit();
            }}
            className="p-1 flex flex-col"
        >
            {content}
            {stepType === 'leftClick' ? (
                <div className="grid">
                    <div className="row-start-1 row-end-2 col-start-1 col-end-3">{fileUploadForm}</div>
                    {children}
                    <div className="row-start-1 row-end-1 col-start-1 col-end-3 pointer-events-none">{runButton}</div>
                </div>
            ) : (
                <>
                    {children}
                    {runButton}
                </>
            )}
        </form>
    );
};

const CustomActionInput: FC<{
    input: ReactElement;
    variables: { [key: string]: string };
    editVariables?: (action: 'add' | 'remove', payload: { [key: string]: string }) => void;
    targetingSystem?: ReactElement;
    targetingStrategy?: TargetingStrategy;
}> = ({ input, variables, editVariables, targetingSystem, targetingStrategy = 'ai' }) => {
    const [open, setOpen] = useState(false);

    const appendVariableToInputValue = (variable: string) => {
        const inputElement = input as ReactElement;
        const inputValue = inputElement.props.value as string;
        const caretPosition = inputElement.props.selectionEnd ?? inputValue.length;
        const newValue = inputValue.slice(0, caretPosition) + `{{${variable}}}` + inputValue.slice(caretPosition);
        inputElement.props.onChange({ target: { value: newValue } } as React.ChangeEvent<HTMLInputElement>);
    };

    return (
        <div className="flex flex-1 items-center gap-2">
            {targetingSystem}
            <div className="relative w-full flex-1 flex items-center">
                {cloneElement(input as ReactElement, {
                    ...getPlaceholderForTargetingStrategy(targetingStrategy),
                    className: cn(input.props.className, 'pe-[50px]'),
                })}
                <Popover open={open} onOpenChange={setOpen}>
                    <PopoverTrigger asChild>
                        <button
                            aria-label='Open "Variables" menu'
                            className={cn(
                                'absolute rounded h-[24px] px-0.5 bg-purple-100 text-purple-900 transition-opacity duration-200 flex items-center justify-center',
                            )}
                            style={{
                                insetInlineEnd: '6px',
                                top: '6px',
                            }}
                        >
                            <span className="text-detail">{'{{var}}'}</span>
                        </button>
                    </PopoverTrigger>

                    <PopoverContent className={'w-[500px] p-0'}>
                        <VariablesPopover
                            variables={variables}
                            editVariables={editVariables}
                            updateInput={appendVariableToInputValue}
                        />
                    </PopoverContent>
                </Popover>
            </div>
        </div>
    );
};

const getHighlightableSelectorsForTargetingStrategy = (
    targetingStrategy: TargetingStrategy,
    target: string,
): { [key: string]: { selector: string; unique: boolean } } => {
    switch (targetingStrategy) {
        case 'xpath':
            return {
                xPath: {
                    selector: target,
                    unique: true,
                },
            };
        case 'datatestid':
            return {
                'data-testid': {
                    selector: target,
                    unique: true,
                },
            };
        case 'arialabel':
            return {
                'aria-label': {
                    selector: target,
                    unique: true,
                },
            };
        case 'playwright':
            return {
                playwrightCode: {
                    selector: target,
                    unique: true,
                },
            };
        case 'css':
        default:
            return {
                fullQualifiedSelector: {
                    selector: target,
                    unique: true,
                },
            };
    }
};

const getPlaceholderForTargetingStrategy = (targetingStrategy: TargetingStrategy): { placeholder: string } | {} => {
    switch (targetingStrategy) {
        case 'xpath':
            return { placeholder: 'Example: //div[@id="myId"]' };
        case 'datatestid':
            return { placeholder: 'Example: my-data-testid' };
        case 'arialabel':
            return { placeholder: 'Example: the Submit button in the search bar' };
        case 'css':
            return { placeholder: 'Example: pre > #myId:first-child' };
        case 'playwright':
            return { placeholder: 'Example: internal:role=button[name="Submit"i]' };
        default:
            return {};
    }
};

const targetingStrategyOptions = [
    { icon: <Sparkles />, label: 'AI locator', key: 'ai' },
    { icon: <CurlyBraces />, label: 'CSS', key: 'css' },
    { icon: <Code2 />, label: 'XPATH', key: 'xpath' },
    { icon: <PersonStanding />, label: 'Data Test ID', key: 'datatestid' },
    { icon: <PersonStanding />, label: 'Aria Label', key: 'arialabel' },
    { icon: <DramaIcon />, label: 'Playwright', key: 'playwright' },
];

export const TargetingStrategySwitcher: FC<{
    value: TargetingStrategy;
    handleSelect: (string: TargetingStrategy) => void;
}> = ({ value, handleSelect }) => {
    const [open, setOpen] = useState(false);

    const items = targetingStrategyOptions;

    const current = items.find((item) => item.key === value);

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Clickable
                    variant="outline"
                    aria-expanded={open}
                    className="bg-white border-slate-300 ps-2 pe-1"
                    aria-label="Select a targeting strategy"
                    title={`Specify how you want to target elements. Heal uses AI by default to make test robusts. Use classic locators for more control (brittle). Currently selected: ${current?.label}`}
                >
                    <span className="flex items-center gap-1 font-medium">
                        {current && cloneElement(current?.icon, { className: 'text-slate-500 shrink-0', size: '18px' })}
                        {current?.label}
                        <CaretSortIcon className="ml-auto h-4 w-4 shrink-0" />{' '}
                    </span>
                </Clickable>
            </PopoverTrigger>
            <PopoverContent className="w-[200px] p-0">
                <Command>
                    <CommandList>
                        <CommandGroup>
                            {items.map((item) => (
                                <CommandItem
                                    key={item.key}
                                    onSelect={() => {
                                        setOpen(false);
                                        handleSelect(item.key as TargetingStrategy);
                                    }}
                                >
                                    <span className="flex items-center gap-1 font-medium text-slate-700">
                                        {cloneElement(item.icon, {
                                            className: 'text-slate-500 shrink-0',
                                            size: '16px',
                                        })}
                                        {item.label}
                                    </span>
                                </CommandItem>
                            ))}
                        </CommandGroup>
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    );
};
